import {
  afterNextRender,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  NgZone,
  output,
  Signal,
  viewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { filter, fromEvent, map, pairwise } from 'rxjs';
import { Breakpoints } from '../../breakpoint';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TopNavigationPhoneComponent } from './phone/top-navigation-phone.component';
import { TopNavigationQuickActionComponent } from './quick-action/top-navigation-quick-action.component';
import { TopNavigationProfileComponent } from './profile/top-navigation-profile.component';
import { HiddenDirective, ScreenSizeService } from '@nestech/pure-ui';
import { cassiopeiaIcons } from '@nestech/utils';
import { EnvironmentService } from '@web/service';

@Component({
  selector: 'cas-top-navigation',
  templateUrl: './top-navigation.component.html',
  styleUrls: ['./top-navigation.component.scss'],
  imports: [
    FaIconComponent,
    TopNavigationPhoneComponent,
    TopNavigationQuickActionComponent,
    TopNavigationProfileComponent,
    RouterLink,
    HiddenDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopNavigationComponent {
  readonly icon = cassiopeiaIcons;
  readonly withMenuToggle = input(true, { transform: booleanAttribute });
  readonly withProfileInfo = input(true, { transform: booleanAttribute });
  readonly menuToggle = output<void>();
  readonly header = viewChild.required<ElementRef<HTMLElement>>('header');
  readonly logo: Signal<string>;

  constructor() {
    const ngZone = inject(NgZone);
    afterNextRender(() => {
      ngZone.runOutsideAngular(() => {
        let transformed = 0;
        const headerElement = this.header().nativeElement;
        fromEvent(window, 'scroll')
          .pipe(
            filter(() => window.innerWidth <= Breakpoints.sm),
            map(() => window.scrollY),
            pairwise(),
          )
          .subscribe(([previous, current]) => {
            if (current > previous && transformed > -64) {
              transformed = Math.max(-64, transformed - (current - previous));
              headerElement.style.transform = `translateY(${transformed}px)`;
            }
            if (current < previous && transformed < 0) {
              transformed = Math.min(0, transformed + (previous - current));
              headerElement.style.transform = `translateY(${transformed}px)`;
            }
          });
      });
    });
    const smLogo = inject(EnvironmentService).environment.logo?.sm ?? 'logo/logo.sm.svg';
    const logo = inject(EnvironmentService).environment.logo?.md ?? 'logo/logo.svg';
    const screenSize = inject(ScreenSizeService).screenSize;
    this.logo = computed(() => (['sm', 'xs'].includes(screenSize() ?? '') ? smLogo : logo));
  }
}
